import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  clearDashboardBridgeCallbackState,
  clearDashboardBridgeRehydrationAttempt,
  exchangeDashboardBridgeCode,
  logoutWithDashboardBridge,
  readDashboardBridgeCallbackState,
  startDashboardBridgeRedirect
} from "./dashboardBridgeAuth.js";
import { clearAuthHint, writeAuthHint } from "./authHint.js";
import configs from "../../configs.js";
import { buildEditorLoginRedirectUrl } from "./xrhubAuthHandoff.js";
import InitialLoading from "../InitialLoading.js";
import Error from "../Error.js";

function normalizeBasePath(basePath) {
  if (!basePath) {
    return "";
  }

  const trimmed = basePath.trim();
  if (!trimmed || trimmed === "/") {
    return "";
  }

  return trimmed.startsWith("/") ? trimmed.replace(/\/+$/, "") : `/${trimmed.replace(/\/+$/, "")}`;
}

function stripBasePath(path) {
  const basePath = normalizeBasePath(configs.ROUTER_BASE_PATH);

  if (!basePath) {
    return path;
  }

  if (path === basePath) {
    return "/";
  }

  return path.startsWith(`${basePath}/`) || path.startsWith(`${basePath}?`) || path.startsWith(`${basePath}#`)
    ? path.slice(basePath.length) || "/"
    : path;
}

function isProtectedReturnPath(path) {
  const pathname = path.split(/[?#]/)[0] || "/";
  return pathname === "/projects" || pathname.startsWith("/projects/");
}

function isBridgeSessionEndedError(error) {
  const status = error && typeof error === "object" && "status" in error ? Number(error.status) : null;
  return status === 401 || status === 403;
}

function resolveBridgeFailureReturnPath(returnTo) {
  const returnUrl = new URL(returnTo, window.location.origin);

  if (returnUrl.origin !== window.location.origin) {
    return null;
  }

  return stripBasePath(`${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`);
}

const DashboardBridgeCallback = ({ api, routeProps }) => {
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const finishBridgeLogin = async () => {
      const params = new URLSearchParams(routeProps.location.search);
      const code = params.get("code");
      const state = params.get("state");
      const bridgeError = params.get("error");
      const callbackState = state ? readDashboardBridgeCallbackState(state) : null;

      if (!state || !callbackState) {
        throw new Error("Missing auth bridge callback state.");
      }

      const completeExpiredSessionFallback = async () => {
        clearAuthHint();
        clearDashboardBridgeRehydrationAttempt(callbackState.returnTo);
        clearDashboardBridgeCallbackState(state);
        const returnPath = resolveBridgeFailureReturnPath(callbackState.returnTo);

        if (!returnPath || isProtectedReturnPath(returnPath)) {
          if (configs.isLocal) {
            await startDashboardBridgeRedirect(callbackState.returnTo);
            return;
          }

          const returnUrl = new URL(callbackState.returnTo, window.location.origin);
          window.location.assign(
            buildEditorLoginRedirectUrl(
              {
                origin: window.location.origin,
                pathname: returnUrl.pathname,
                search: returnUrl.search,
                hash: returnUrl.hash
              },
              {
                xrhubUrl: configs.XRHUB_URL
              }
            )
          );
          return;
        }

        routeProps.history.replace(returnPath);
      };

      if (bridgeError === "account_suspended") {
        clearAuthHint();
        clearDashboardBridgeRehydrationAttempt(callbackState.returnTo);
        clearDashboardBridgeCallbackState(state);
        logoutWithDashboardBridge({ logoutParams: { returnTo: callbackState.returnTo } });
        return;
      }

      if (!code) {
        await completeExpiredSessionFallback();
        return;
      }

      try {
        const token = await exchangeDashboardBridgeCode(code, state, callbackState.codeVerifier);
        const auth0User = await api.getAuth0User(token);
        const email = auth0User?.email;

        if (!email) {
          throw new Error("Auth bridge user response did not include an email.");
        }

        const abortController = new AbortController();
        await api.authenticate(email, abortController.signal, token);
        writeAuthHint(auth0User);
        clearDashboardBridgeRehydrationAttempt(callbackState.returnTo);
        clearDashboardBridgeCallbackState(state);

        if (!cancelled) {
          const returnUrl = new URL(callbackState.returnTo || "/", window.location.origin);
          if (returnUrl.origin === window.location.origin) {
            routeProps.history.replace(stripBasePath(`${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`));
            return;
          }

          window.location.assign(returnUrl.toString());
        }
      } catch (bridgeError) {
        if (isBridgeSessionEndedError(bridgeError)) {
          await completeExpiredSessionFallback();
          return;
        }

        clearDashboardBridgeCallbackState(state);
        throw bridgeError;
      }
    };

    finishBridgeLogin().catch(callbackError => {
      if (!cancelled) {
        setError(callbackError);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [api, routeProps.history, routeProps.location.search]);

  if (error) {
    return <Error message={error.message || "Authentication failed"} />;
  }

  return <InitialLoading fullScreen api={api} />;
};

DashboardBridgeCallback.propTypes = {
  api: PropTypes.shape({
    authenticate: PropTypes.func.isRequired,
    getAuth0User: PropTypes.func.isRequired
  }).isRequired,
  routeProps: PropTypes.shape({
    history: PropTypes.shape({
      replace: PropTypes.func.isRequired
    }).isRequired,
    location: PropTypes.shape({
      search: PropTypes.string.isRequired
    }).isRequired
  }).isRequired
};

export default DashboardBridgeCallback;
